package dev.akshaychordiya.content

/**
 * A place a talk was given. Coordinates are used verbatim by the world map, which projects them equirectangularly,
 * so they only need to be accurate to about a degree.
 */
class City(val name: String, val country: String, val lat: Double, val lon: Double)

object Cities {
    val London = City("London", "United Kingdom", 51.51, -0.13)
    val Paris = City("Paris", "France", 48.86, 2.35)
    val NewYork = City("New York", "United States", 40.71, -74.01)
    val Orlando = City("Orlando", "United States", 28.54, -81.38)
    val SanFrancisco = City("San Francisco", "United States", 37.77, -122.42)
    val Berlin = City("Berlin", "Germany", 52.52, 13.40)
    val Nairobi = City("Nairobi", "Kenya", -1.29, 36.82)
    val Bengaluru = City("Bengaluru", "India", 12.97, 77.59)
    val Mumbai = City("Mumbai", "India", 19.08, 72.88)
    val Pune = City("Pune", "India", 18.52, 73.86)
}

/**
 * Someone the talk was given jointly with.
 *
 * @param name As it appeared on the title card, not as it appears on their profile - the slides say "Tasha Ramesh"
 *   even though the LinkedIn handle differs, and the credit should match what the audience saw.
 * @param profileUrl Optional. When set, the name links out, so a co-presenter credit points somewhere instead of
 *   being a dead string.
 */
class CoSpeaker(val name: String, val profileUrl: String? = null)

/** Declared once each, so a profile URL is never duplicated across the talks that share a co-presenter. */
object CoSpeakers {
    val TashaRamesh = CoSpeaker("Tasha Ramesh", "https://www.linkedin.com/in/akshataramesh/")
}

enum class TalkKind(val label: String) {
    KEYNOTE("Keynote"),
    TALK("Talk"),
    WORKSHOP("Workshop"),
    ROUNDTABLE("Roundtable"),
    PANEL("Panel"),
    PODCAST("Podcast"),
}

/**
 * @param city `null` for online-only events. Those still appear in the list but are not plotted on the map, since
 *   pinning a remote talk to a city would misrepresent where he has actually spoken.
 * @param videoId A YouTube id, when a recording exists.
 * @param coSpeaker Set when the talk was given jointly - see [CoSpeaker].
 */
class Talk(
    val title: String,
    val event: String,
    val year: Int,
    val date: String,
    val city: City?,
    val kind: TalkKind = TalkKind.TALK,
    val videoId: String? = null,
    val slidesUrl: String? = null,
    val coSpeaker: CoSpeaker? = null,
    val description: String = "",
) {
    /**
     * Preview frame for the talk, or `null` when there is no recording.
     *
     * These are YouTube's own `mqdefault` frames (a clean 320x180, unlike `hqdefault` which letterboxes to 4:3),
     * downloaded into the repo as WebP rather than hotlinked from `i.ytimg.com`, so the page makes no third-party
     * requests.
     */
    val thumbnailUrl: String? get() = videoId?.let { "/images/talks/$it.webp" }
}

/**
 * Akshay's speaking history, newest first.
 *
 * Most entries were reconciled against the "Android / Kotlin talks 🎤" YouTube playlist, so the recording ids and
 * hosting channels are accurate.
 *
 * Everything on the /talks page - the counts, the map, the country and city filters - recomputes from this list, so
 * adding a talk is a single entry here and nothing else. Keep it ordered newest first: the home page takes the top
 * three.
 */
val talks = listOf(
    Talk(
        title = "Passwords are out. Passkeys are in 🧬",
        event = "droidcon Orlando",
        year = 2026,
        date = "16 July 2026",
        city = Cities.Orlando,
        kind = TalkKind.ROUNDTABLE,
        description = "What passkeys are and how FIDO2 / WebAuthn works behind the scenes; how they cut a company's " +
            "operational cost; implementing passkey auth on Android and iOS including biometric flows; backend and " +
            "identity-provider considerations; and the platform-specific pitfalls that are painful to debug.",
    ),
    Talk(
        title = "App Clinic",
        event = "Android Makers by droidcon",
        year = 2026,
        date = "April 2026",
        city = Cities.Paris,
        kind = TalkKind.ROUNDTABLE,
        description = "Live review of apps and projects submitted by attendees, with practical advice on how to " +
            "improve them.",
    ),
    Talk(
        title = "Thriving in an AI era",
        event = "Android Makers by droidcon",
        year = 2026,
        date = "April 2026",
        city = Cities.Paris,
        kind = TalkKind.KEYNOTE,
        videoId = "Vuwxcis8-wQ",
        description = "How AI, Kotlin Multiplatform and the wider platform shift reshape what an Android career " +
            "looks like - and what to do about it.",
    ),
    Talk(
        title = "Boosting Compose UI from Sluggish to Snappy",
        event = "droidcon India",
        year = 2025,
        date = "December 2025",
        city = Cities.Bengaluru,
        description = "Diagnosing and fixing Compose performance problems - tooling, debugging workflow, and the " +
            "team practices that stop regressions coming back.",
    ),
    Talk(
        title = "Fix Sluggish Compose UI: Live Performance Diagnosis",
        event = "nextapp devCon",
        year = 2025,
        date = "November 2025",
        city = null,
        kind = TalkKind.ROUNDTABLE,
        videoId = "RVS4Uynb_rE",
        description = "A live, unscripted pass over real Compose performance problems using the profiler and " +
            "recomposition counts.",
    ),
    // No recording exists, so no videoId: the card renders without a thumbnail or a "Watch" link, the same way the
    // upcoming Orlando roundtable does.
    Talk(
        title = "Boosting Compose UI from Sluggish to Snappy",
        event = "droidcon Kenya",
        year = 2025,
        date = "6 November 2025",
        city = Cities.Nairobi,
        description = "Real-world strategies for diagnosing and fixing Compose performance problems - tooling, " +
            "debugging workflow, and the team practices that stop regressions coming back.",
    ),
    Talk(
        title = "Form Frenzy to Form Framework",
        event = "droidcon London",
        year = 2025,
        date = "October 2025",
        city = Cities.London,
        videoId = "hf1_fCT2O8M",
        description = "Building a reusable form architecture in Compose: dynamic rendering, validation, state " +
            "management with UDF and Flow, navigation, and keeping it fast as it grows.",
    ),
    Talk(
        title = "Boosting Compose UI from Sluggish to Snappy",
        event = "droidcon San Francisco",
        year = 2024,
        date = "June 2024",
        city = Cities.SanFrancisco,
        videoId = "1tAUmuTXltE",
        coSpeaker = CoSpeakers.TashaRamesh,
        description = "Where Compose performance actually goes wrong at scale, and the tooling and habits that " +
            "catch it before your users do.",
    ),
    Talk(
        title = "Boosting Compose UI from Sluggish to Snappy",
        event = "Android Makers by droidcon",
        year = 2024,
        date = "April 2024",
        city = Cities.Paris,
        videoId = "yzQ9n7SGnWU",
        coSpeaker = CoSpeakers.TashaRamesh,
        description = "Deferred state reads, stability, and the measurements that tell you whether a Compose " +
            "screen is genuinely fast.",
    ),
    Talk(
        title = "Boosting Compose UI from Sluggish to Snappy",
        event = "droidcon NYC",
        year = 2023,
        date = "September 2023",
        city = Cities.NewYork,
        videoId = "mIZnyGj4V6c",
        coSpeaker = CoSpeakers.TashaRamesh,
        description = "Real-world strategies for diagnosing and fixing Compose performance problems - tooling, " +
            "debugging workflow, and the team practices that stop regressions coming back.",
    ),
    Talk(
        title = "Go with the Flow",
        event = "droidcon San Francisco",
        year = 2023,
        date = "June 2023",
        city = Cities.SanFrancisco,
        videoId = "YaMqcsT4Ln8",
        description = "Kotlin Flow from first principles - why it exists, how it behaves, and where it fits.",
    ),
    Talk(
        title = "Go with the Flow",
        event = "Android Makers by droidcon",
        year = 2023,
        date = "April 2023",
        city = Cities.Paris,
        videoId = "pgDK4-frLzo",
        description = "How Kotlin Flow actually works, how to migrate onto it from RxJava and callbacks, and how " +
            "it fits with Compose and testing.",
    ),
    Talk(
        title = "Working abroad - everything you need to know",
        event = "Curated Reality",
        year = 2021,
        date = "June 2021",
        city = null,
        kind = TalkKind.PANEL,
        videoId = "jqfGPdYmtZs",
        description = "The honest version of relocating as an engineer: visas, timing, money, and the parts that " +
            "are harder than the paperwork.",
    ),
    Talk(
        title = "Go with the Flow",
        event = "Android Summit",
        year = 2020,
        date = "October 2020",
        city = null,
        videoId = "-Aoc5mztqAI",
        description = "The history and design of Kotlin Flow, its basics, and how it compares to other reactive " +
            "stream implementations.",
    ),
    Talk(
        title = "Go with the Flow",
        event = "GDG Berlin Android",
        year = 2020,
        date = "August 2020",
        city = null,
        videoId = "aMGr7hf0Ui8",
        description = "A community-meetup walkthrough of Kotlin Flow and coroutine-based reactive streams.",
    ),
    Talk(
        title = "Go with the Flow 🌊",
        event = "360|AnDev",
        year = 2020,
        date = "August 2020",
        city = null,
        videoId = "8775WhcNPd0",
        description = "Kotlin Flow explained for teams already invested in RxJava, and what a migration really " +
            "costs.",
    ),
    Talk(
        title = "Go with the Flow",
        event = "Kotlin Mumbai",
        year = 2020,
        date = "April 2020",
        city = Cities.Mumbai,
        videoId = "vBApb3ISyuA",
        description = "The Kotlin Mumbai meetup edition of the Flow talk, with a longer Q&A.",
    ),
    Talk(
        title = "MVI made for Android",
        event = "droidcon Berlin",
        year = 2019,
        date = "2019",
        city = Cities.Berlin,
        videoId = "INJ4esYmLLY",
        description = "Why MVI, what it actually buys you, and an honest look at the libraries offering their own " +
            "take on it for Android.",
    ),
    Talk(
        title = "Architecture Components",
        event = "Fragmented Podcast #115",
        year = 2018,
        date = "July 2018",
        city = null,
        kind = TalkKind.PODCAST,
        videoId = "ijjGWaL3E14",
        description = "A long-form conversation about Room, LiveData and the Architecture Components as they " +
            "landed.",
    ),
    Talk(
        title = "Exploring Architecture Components",
        event = "GDG Mumbai DevFest",
        year = 2017,
        date = "December 2017",
        city = Cities.Mumbai,
        videoId = "nYbX6BNF9oA",
        description = "A first tour of Room, ViewModel and LiveData, shortly after Google announced them.",
    ),
    Talk(
        title = "Getting Started with Kotlin",
        event = "Hackerspace Mumbai",
        year = 2017,
        date = "August 2017",
        city = Cities.Mumbai,
        videoId = "TmNs8xLpUkE",
        description = "An introduction to Kotlin for Android developers, given the year it became an official " +
            "Android language.",
    ),
)

/** The full recording archive, linked from /talks for anyone who wants to binge. */
const val TALKS_PLAYLIST_URL =
    "https://www.youtube.com/playlist?list=PLEnKay1Mvpqu-vfdH-v71V0xtxrczKiRG"
